import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';
import Vue from 'vue';
import Int64LookupModel from '@glittr/frontend-core/src/services/v2/model/int64-lookup-model';
import PostFeedbackRequestModel from '../../../../services/v2/model/post-feedback-request-model';
import PostExcelForAdminRequestModel from '../../../../services/v2/model/post-excel-for-admin-request-model';
import GetCompanyFeedbacksRequestModel from '../../../../services/v2/model/get-company-feedbacks-request-model';
import LookupRequestModel from '../../../../services/v2/model/lookup-request-model';
import GuidEventLookupModel from '../../../../services/v2/model/guid-event-lookup-model';

export default class FeedbacksPage extends PageBase {
  public get adminFeedbacksList() {
    if (this.adminFeedbacksDataSource.data !== undefined && this.adminFeedbacksDataSource.data.items !== undefined) {
      return this.adminFeedbacksDataSource.data?.items?.map((record) => ({
        id: record.data.id,
        companyName: record.data.companyName,
        organisationName: record.data.organisationName,
        eventRating: Vue.$tEnum('page.dashboard.companies.feedbacks.enum.eventRating', record.data.eventRating!),
        reparticipationRating: Vue.$tEnum('page.dashboard.companies.feedbacks.enum.reparticipationRating', record.data.reparticipationRating!),
        youthVisitorsCount: record.data.youthVisitorsCount,
        adultVisitorsCount: record.data.adultVisitorsCount,
        remark: (record.data.remark?.length ?? 0) > 10 ? `${record.data.remark?.substring(0, 10)}...` : (record.data.remark || ''),
      }));
    }
    return [];
  }

  public set adminFeedbacksList(value) { /* Do nothing */ }

  public createFeedbackModel: PostFeedbackRequestModel = new PostFeedbackRequestModel();

  public isLoadingExcel = false;

  public organisationNameItems: Int64LookupModel[] | undefined;

  public eventItems: GuidEventLookupModel[] | undefined;

  public adminFeedbacksDataSource = new DataSource({
    selectCommand: () => {
      const request = new GetCompanyFeedbacksRequestModel();
      request.eventIdentifier = this.eventsDataSource.filter.eventIdentifier;
      request.selectedOrganisationId = this.organisationsDataSource.filter.selectedOrganisationId;
      request.searchCriteria = this.adminFeedbacksDataSource.filter.searchCriteria;
      request.paging.currentPage = this.adminFeedbacksDataSource.paging.currentPage;
      request.paging.pageSize = this.adminFeedbacksDataSource.paging.pageSize;
      request.paging.includeCount = true;
      return Vue.$service.v2.api.tdg_Feedbacks.getCompanyFeedbacks(request);
    },
    insertCommand: () => {
      const request = new PostExcelForAdminRequestModel();
      request.eventIdentifier = this.eventsDataSource.filter.eventIdentifier;
      request.selectedOrganisationId = this.organisationsDataSource.filter.selectedOrganisationId;
      request.searchCriteria = this.adminFeedbacksDataSource.filter.searchCriteria;
      return Vue.$service.v2.api.tdg_Feedbacks.postExcelForAdmin(request);
    },
  });

  public eventsDataSource = new DataSource({
    selectCommand: () => {
      const req = new LookupRequestModel();
      return Vue.$service.v2.api.tdg_Events.eventLookup(req);
    },
  });

  public organisationsDataSource = new DataSource({
    selectCommand: () => {
      const req = new LookupRequestModel();
      req.eventIdentifier = this.eventsDataSource.filter.eventIdentifier;
      return Vue.$service.v2.api.tdg_Organisations.lookup(req);
    },
  });

  async initialize(): Promise<void> {
    await this.eventsDataSource.select();
    await this.loadEventLookup();
    await this.organisationsDataSource.select();
    await this.loadOrganisationLookup();
    await this.adminFeedbacksDataSource.select();
  }

  async refreshInitialize(): Promise<void> {
    await this.organisationsDataSource.select();
    await this.loadOrganisationLookup();
    await this.adminFeedbacksDataSource.select();
  }

  public feedbackColumns = [
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.id'), field: 'id' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.companyName'), field: 'companyName' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.organisationName'), field: 'organisationName' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.eventRating'), field: 'eventRating' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.reparticipationRating'), field: 'reparticipationRating' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.youthVisitorsCount'), field: 'youthVisitorsCount' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.adultVisitorsCount'), field: 'adultVisitorsCount' },
    { value: Vue.$t('page.dashboard.adminFeedbacks.table.remark'), field: 'remark' },
  ];

  public async downloadExcel() {
    this.isLoadingExcel = true;

    if (this.adminFeedbacksDataSource.data) {
      if (this.adminFeedbacksDataSource.data.items.length > 0) {
        const requestModel = new PostExcelForAdminRequestModel({
          selectedOrganisationId: this.organisationsDataSource.filter.selectedOrganisationId,
          searchCriteria: this.adminFeedbacksDataSource.filter.searchCriteria,
          eventIdentifier: this.eventsDataSource.filter.eventIdentifier,
        });

        const response = await this.adminFeedbacksDataSource.insert(requestModel);

        if (response && response.data) {
          const blob = new Blob([response.data], { type: 'application/excel' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = 'Tour-de-Gwärb-Feedbacks.xlsx';
          link.click();
        }
      }
    }
    this.isLoadingExcel = false;
  }

  async refreshFeedbacks() {
    try {
      await this.refreshInitialize();
    } catch (error) {
      Vue.$log.error(`Error refreshing companies: ${error}`);
    }
  }

  async loadOrganisationLookup() {
    try {
      if (this.organisationsDataSource.data) {
        const res = this.organisationsDataSource.data;
        this.organisationNameItems = res.items.map((i: { data: any; }) => i.data);
      }
    } catch (error) {
      Vue.$log.error('Failed to load organisations:');
    }
  }

  async loadEventLookup() {
    if (this.eventsDataSource.data) {
      const res = this.eventsDataSource.data;

      this.eventItems = res.items.map((i) => {
        const model = new GuidEventLookupModel();
        model.id = i.data.id;
        model.caption = i.data.caption;
        return model;
      });

      if (this.eventItems.length > 0) {
        const lastItem = this.eventItems[this.eventItems.length - 1];
        this.eventsDataSource.filter.eventIdentifier = lastItem.id;
      }
    }
  }
}
